import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Alert,
    Button,
    Container,
    Form,
    Row,
    Col,
    Spinner,
} from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import GuideLink from '@/components/common/GuideLink';
import PreviewLockNote from '@/components/common/PreviewLockNote';
import { useTerm } from '@/shared/contexts/TermContext';
import {
    StudentWindowClosed,
    StudentWindowCloses,
} from '@/components/common/StudentWindowNotice';
import { studentWindowFor } from '@/shared/utils/studentWindow';
import { usePreviewLock } from '@/shared/hooks/usePreviewLock';
import useMyCandidacies from '@/shared/hooks/useMyCandidacies';
import useStudentEmploymentRules from '@/shared/hooks/useStudentEmploymentRules';
import { getProgramRequirements } from '@/shared/services/programService';
import { candidacyService } from '@/shared/services/candidacyService';
import positionService from '@/shared/services/positionService';
import studentApplicationService from '@/shared/services/studentApplicationService';
import {
    OPEN_CANDIDACY_STAGES,
    PROFILE_APPLYING_STATUSES,
    candidacyFor,
} from '@/shared/utils/candidacyStage';
import {
    PROGRAM_NAMES,
    formatReleaseMoment,
    releaseMoment,
} from '@/shared/utils/deadline';
import {
    DAY_FILTERS,
    TIME_OF_DAY_FILTERS,
    matchesDays,
    matchesModality,
    matchesTimeOfDay,
} from '@/shared/utils/positionFilters';
import { positionCourse } from '@/shared/utils/positionText';
import { trainingLine } from '@/shared/utils/trainingSessions';
import { STUDENT_APP_STATUS } from '@/shared/utils/studentApplicationStatus';
import PositionCard from './PositionCard';
import './OpenPositionsPage.css';
import { termDisplayName } from '@/shared/utils/termLabel';

const STUDENT_POSITION_PROGRAMS = ['la'];
const DEFAULT_PROGRAM = 'la';
const BLOCKED_PROFILE_STATUSES = ['withdrawn', 'rejected', 'declined'];

/**
 * What a card offers, from the student's application for the term and their
 * candidacy for this position. `profile` is null when they have none.
 */
export function applyStateFor({ position, profile, candidacies }) {
    const candidacy = candidacyFor(candidacies, {
        applicationId: position.applicationId,
        program: position.program,
    });
    const profileId = profile?._id;
    const status = profile?.status;

    if (status && BLOCKED_PROFILE_STATUSES.includes(status)) {
        return candidacy ? { kind: 'stage', candidacy } : { kind: 'none' };
    }
    if (candidacy && status !== 'draft') {
        if (candidacy.stage === 'withdrawn') return { kind: 'resubmit' };
        if (OPEN_CANDIDACY_STAGES.includes(candidacy.stage)) {
            return { kind: 'applied', candidacy, profileId };
        }
        return { kind: 'stage', candidacy };
    }
    if (!profile) {
        return {
            kind: 'link',
            label: 'Start LA application',
            to: `/apply?program=${position.program}&position=${position.applicationId}`,
        };
    }
    if (status === 'draft') {
        return candidacy
            ? { kind: 'stage', candidacy }
            : {
                  kind: 'link',
                  label: 'Finish LA application',
                  to: `/apply/${profileId}/edit`,
              };
    }
    if (PROFILE_APPLYING_STATUSES.includes(status)) return { kind: 'apply' };
    return { kind: 'none' };
}

// The client only narrows what the server sent: a coordinator or admin
// receives pending and closed positions too, which a student never would.
const isListed = (position) =>
    position.openSlots > 0 &&
    (position.status ?? 'open') === 'open' &&
    (position.released ?? true) === true;

function matchesText(position, text) {
    const course = positionCourse(position).toLowerCase();
    const haystacks = [
        course,
        course.replace(/\s+/g, ''),
        position.title,
        position.instructorName,
        position.recruitingMessage,
    ];
    return haystacks.some((h) => h && h.toLowerCase().includes(text));
}

function profileNotice(profile, termName, program, windowClosed) {
    if (!profile) {
        // After the close the page's closed notice says it all; telling the
        // student to fill in an application they can no longer start
        // contradicts it.
        if (windowClosed) return null;
        return {
            text: `Before you apply to a position, fill in your LA application for ${termName}. You can reuse your application for every position you apply to.`,
            link: {
                to: `/apply?program=${program}`,
                text: 'Start your LA application',
            },
        };
    }
    if (profile.status === 'draft') {
        return {
            text: 'Finish your LA application to apply to positions.',
            link: {
                to: `/apply/${profile._id}/edit`,
                text: 'Finish your application',
            },
        };
    }
    if (BLOCKED_PROFILE_STATUSES.includes(profile.status)) {
        const label = (
            STUDENT_APP_STATUS[profile.status]?.label || profile.status
        ).toLowerCase();
        return {
            text: `Your LA application for ${termName} is ${label}, so you can't apply to positions this term.`,
        };
    }
    return null;
}

// After the student window closes, nothing that starts or restarts an
// application is offered. A candidacy's stage, and the withdraw that comes
// with an application in progress, stay.
const STARTING_KINDS = ['link', 'apply', 'resubmit'];
const closeStart = (state, windowClosed) =>
    windowClosed && STARTING_KINDS.includes(state.kind)
        ? { kind: 'none' }
        : state;

export default function OpenPositionsPage() {
    const { currentTerm, currentTermCode, termsLoading } = useTerm();
    const [searchParams] = useSearchParams();
    const requestedProgram = searchParams.get('program');
    const program = STUDENT_POSITION_PROGRAMS.includes(requestedProgram)
        ? requestedProgram
        : DEFAULT_PROGRAM;
    const locked = usePreviewLock();

    const [positionsState, setPositionsState] = useState({
        status: 'loading',
        positions: [],
    });
    const [positionsAttempt, setPositionsAttempt] = useState(0);
    const [profileState, setProfileState] = useState({
        status: 'loading',
        profile: null,
    });
    const [profileAttempt, setProfileAttempt] = useState(0);
    const candidaciesState = useMyCandidacies();
    const { candidacies, commitments, replace } = candidaciesState;
    const { rules } = useStudentEmploymentRules();

    const [subject, setSubject] = useState(searchParams.get('subject') || '');
    const [courseNumber, setCourseNumber] = useState(
        searchParams.get('courseNumber') || ''
    );
    const [query, setQuery] = useState('');
    const [days, setDays] = useState([]);
    const [timeOfDay, setTimeOfDay] = useState('');
    const [modality, setModality] = useState('');
    const [filtersOpen, setFiltersOpen] = useState(false);
    const focusId = searchParams.get('position');
    const focusRef = useRef(null);
    const focusDone = useRef(false);
    const inFlight = useRef(new Set());

    useEffect(() => {
        if (!currentTermCode) return undefined;
        let cancelled = false;
        positionService
            .listPositions({ program, term: currentTermCode })
            .then((data) => {
                if (cancelled) return;
                setPositionsState({
                    status: 'ready',
                    positions: data.positions || [],
                });
            })
            .catch(() => {
                if (cancelled) return;
                // A refresh after a refusal keeps the list already on screen.
                setPositionsState((previous) =>
                    previous.status === 'ready'
                        ? previous
                        : { status: 'error', positions: [] }
                );
            });
        return () => {
            cancelled = true;
        };
    }, [currentTermCode, program, positionsAttempt]);

    useEffect(() => {
        if (!currentTermCode) return undefined;
        let cancelled = false;
        studentApplicationService
            .getMyApplications({ term: currentTermCode, program })
            .then((data) => {
                if (cancelled) return;
                setProfileState({
                    status: 'ready',
                    profile: data?.[0] || null,
                });
            })
            .catch(() => {
                if (cancelled) return;
                setProfileState({ status: 'error', profile: null });
            });
        return () => {
            cancelled = true;
        };
    }, [currentTermCode, program, profileAttempt]);

    // Whether the program asks the course question, which the apply
    // confirmation then asks too. A failed read leaves the confirmation
    // unable to submit until a retry succeeds: sending without knowing the
    // question would end in a refusal the student cannot act on.
    const [askCourse, setAskCourse] = useState(false);
    const [trainingSessions, setTrainingSessions] = useState([]);
    const [requirementsFailed, setRequirementsFailed] = useState(false);
    const [requirementsAttempt, setRequirementsAttempt] = useState(0);
    useEffect(() => {
        if (!currentTermCode) return undefined;
        let cancelled = false;
        getProgramRequirements(program, currentTermCode)
            .then((data) => {
                if (!cancelled) {
                    setAskCourse(
                        data?.studentAssertions?.priorCompletion === true
                    );
                    setTrainingSessions(data?.trainingSessions || []);
                    setRequirementsFailed(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAskCourse(false);
                    setTrainingSessions([]);
                    setRequirementsFailed(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [currentTermCode, program, requirementsAttempt]);
    const retryRequirements = useCallback(
        () => setRequirementsAttempt((n) => n + 1),
        []
    );

    const listed = useMemo(
        () => positionsState.positions.filter(isListed),
        [positionsState.positions]
    );
    const subjects = useMemo(
        () => [...new Set(listed.map((p) => p.subject).filter(Boolean))].sort(),
        [listed]
    );
    const visible = useMemo(() => {
        const text = query.trim().toLowerCase();
        return listed.filter(
            (p) =>
                (!subject || p.subject === subject) &&
                (!courseNumber || p.courseNumber === courseNumber) &&
                (!text || matchesText(p, text)) &&
                matchesDays(p, days) &&
                matchesTimeOfDay(p, timeOfDay) &&
                matchesModality(p, modality)
        );
    }, [listed, subject, courseNumber, query, days, timeOfDay, modality]);

    const loading =
        termsLoading ||
        (Boolean(currentTermCode) &&
            (positionsState.status === 'loading' ||
                profileState.status === 'loading' ||
                candidaciesState.loading));
    const positionsFailed = positionsState.status === 'error';
    const readFailed =
        profileState.status === 'error' || candidaciesState.error;

    const focusTarget = !loading && !positionsFailed ? focusId : null;
    const focusMissing =
        Boolean(focusTarget) &&
        !listed.some((p) => p.applicationId === focusTarget);

    useEffect(() => {
        if (!focusTarget || focusDone.current) return;
        focusDone.current = true;
        focusRef.current?.focus();
    });

    const refreshPositions = useCallback(
        () => setPositionsAttempt((n) => n + 1),
        []
    );

    const retryPositions = () => {
        setPositionsState({ status: 'loading', positions: [] });
        refreshPositions();
    };

    const retryReads = () => {
        setProfileState({ status: 'loading', profile: null });
        setProfileAttempt((n) => n + 1);
        candidaciesState.reload();
    };

    const onApply = async (position, { resubmit, answers }) => {
        const id = position.applicationId;
        if (inFlight.current.has(id)) return false;
        inFlight.current.add(id);
        try {
            const candidacy = resubmit
                ? await candidacyService.resubmit(id, program, answers)
                : await candidacyService.create(id, program, answers);
            replace(candidacy);
            return true;
        } catch (error) {
            if (error?.response?.status === 404) refreshPositions();
            throw error;
        } finally {
            inFlight.current.delete(id);
        }
    };

    const clearFilters = () => {
        setSubject('');
        setCourseNumber('');
        setQuery('');
        setDays([]);
        setTimeOfDay('');
        setModality('');
    };
    const toggleDay = (key) =>
        setDays((current) =>
            current.includes(key)
                ? current.filter((d) => d !== key)
                : [...current, key]
        );
    const filtering = Boolean(
        subject ||
        courseNumber ||
        query.trim() ||
        days.length ||
        timeOfDay ||
        modality
    );

    const programName = PROGRAM_NAMES[program];
    const windowClosed = !studentWindowFor(currentTerm, program).accepting;
    const termName = termDisplayName(currentTerm) || 'this term';
    const hoursPerWeek =
        rules?.programLimits?.[program]?.hoursPerWeekPerPosition ?? null;
    const notice = readFailed
        ? null
        : profileNotice(profileState.profile, termName, program, windowClosed);
    const releaseDate =
        currentTerm?.programWindows?.[program]?.decisionsReleased;
    const releaseAt = releaseMoment(releaseDate);
    const notReleased = Boolean(releaseAt) && releaseAt.getTime() > Date.now();
    const confirm = {
        askCourse,
        requirementsFailed,
        retryRequirements,
        trainingText: trainingLine(
            trainingSessions,
            profileState.profile?.laDetails?.isReturning === true
        ),
        accepted:
            commitments?.[currentTermCode]?.programs?.[program]?.positions ?? 0,
        maxPositions:
            rules?.programLimits?.[program]?.maxPositionsPerStudent ?? null,
        applicationLink: profileState.profile?._id
            ? `/my-applications/${profileState.profile._id}`
            : null,
    };
    const narrowing = [subject, courseNumber].filter(Boolean).join(' ');

    let body;
    if (loading) {
        body = (
            <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading positions</span>
            </Spinner>
        );
    } else if (positionsFailed) {
        body = (
            <Alert variant="danger">
                <p className="mb-2">Open positions couldn&apos;t be loaded.</p>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={retryPositions}
                >
                    Try again
                </Button>
            </Alert>
        );
    } else if (listed.length === 0) {
        body = (
            <p>
                {notReleased
                    ? `${program.toUpperCase()} positions open on ${formatReleaseMoment(releaseDate)}, when the ${program.toUpperCase()} program releases its decisions.`
                    : `No ${program.toUpperCase()} position has a place open right now. Places open as sections are awarded and as students decline or withdraw, so check back.`}
            </p>
        );
    } else {
        body = (
            <>
                {focusMissing && (
                    <Alert variant="info">
                        That position isn&apos;t open now.
                    </Alert>
                )}
                <Button
                    variant="outline-dark"
                    className="d-md-none w-100 mb-3 psh-filter-toggle"
                    aria-expanded={filtersOpen}
                    aria-controls="positions-filters"
                    onClick={() => setFiltersOpen((open) => !open)}
                >
                    {filtersOpen ? 'Hide filters' : 'Filter positions'}
                    {filtering && !filtersOpen ? ' (on)' : ''}
                </Button>
                <Form
                    id="positions-filters"
                    role="search"
                    aria-label="Filter positions"
                    className={`mb-3 ${filtersOpen ? '' : 'd-none'} d-md-block`}
                    onSubmit={(e) => e.preventDefault()}
                >
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Group controlId="positions-subject">
                                <Form.Label>Subject</Form.Label>
                                <Form.Select
                                    value={subject}
                                    onChange={(e) => {
                                        setSubject(e.target.value);
                                        setCourseNumber('');
                                    }}
                                >
                                    <option value="">All subjects</option>
                                    {subjects.map((s) => (
                                        <option key={s} value={s}>
                                            {s}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Group controlId="positions-time-of-day">
                                <Form.Label>Time of day</Form.Label>
                                <Form.Select
                                    value={timeOfDay}
                                    onChange={(e) =>
                                        setTimeOfDay(e.target.value)
                                    }
                                >
                                    <option value="">Any time</option>
                                    {TIME_OF_DAY_FILTERS.map((t) => (
                                        <option key={t.key} value={t.key}>
                                            {t.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col xs={6} md={3}>
                            <Form.Group controlId="positions-modality">
                                <Form.Label>Where</Form.Label>
                                <Form.Select
                                    value={modality}
                                    onChange={(e) =>
                                        setModality(e.target.value)
                                    }
                                >
                                    <option value="">Anywhere</option>
                                    <option value="in_person">In person</option>
                                    <option value="online">Online</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={3}>
                            <Form.Group controlId="positions-search">
                                <Form.Label>Search positions</Form.Label>
                                <Form.Control
                                    type="search"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                />
                            </Form.Group>
                        </Col>
                        <Col xs={12}>
                            <div
                                role="group"
                                aria-label="Meeting days"
                                className="d-flex flex-wrap align-items-center gap-2"
                            >
                                <span className="form-label mb-0 me-1">
                                    Days
                                </span>
                                {DAY_FILTERS.map((day) => (
                                    <Button
                                        key={day.key}
                                        variant={
                                            days.includes(day.key)
                                                ? 'primary'
                                                : 'outline-primary'
                                        }
                                        size="sm"
                                        className="psh-filter-chip"
                                        aria-pressed={days.includes(day.key)}
                                        onClick={() => toggleDay(day.key)}
                                    >
                                        {day.label}
                                    </Button>
                                ))}
                                {filtering && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={clearFilters}
                                    >
                                        Clear filters
                                    </Button>
                                )}
                            </div>
                        </Col>
                    </Row>
                </Form>
                <p role="status" className="text-body-secondary">
                    {`Showing ${visible.length} of ${listed.length} position${listed.length === 1 ? '' : 's'}.`}
                </p>
                {courseNumber && (
                    <p className="d-flex flex-wrap align-items-center gap-2">
                        <span>{`Showing ${narrowing} only.`}</span>
                        <Button
                            variant="link"
                            className="p-0"
                            onClick={() => {
                                setSubject('');
                                setCourseNumber('');
                            }}
                        >
                            Show all positions
                        </Button>
                    </p>
                )}
                {visible.length === 0 ? (
                    <p>
                        {`No positions match these filters. Clear them to see all ${listed.length}.`}
                    </p>
                ) : (
                    <Row as="ul" xs={1} lg={2} className="list-unstyled g-3">
                        {visible.map((position) => (
                            <Col as="li" key={position.applicationId}>
                                <PositionCard
                                    position={position}
                                    hoursPerWeek={hoursPerWeek}
                                    applyState={
                                        readFailed
                                            ? { kind: 'none' }
                                            : closeStart(
                                                  applyStateFor({
                                                      position,
                                                      profile:
                                                          profileState.profile,
                                                      candidacies,
                                                  }),
                                                  windowClosed
                                              )
                                    }
                                    onApply={onApply}
                                    confirm={confirm}
                                    locked={locked}
                                    headingRef={
                                        position.applicationId === focusTarget
                                            ? focusRef
                                            : undefined
                                    }
                                />
                            </Col>
                        ))}
                    </Row>
                )}
            </>
        );
    }

    return (
        <Container className="py-4">
            <h1>Open Positions</h1>
            <p className="lead">
                {`${programName} positions${currentTerm ? ` for ${termDisplayName(currentTerm)}` : ''}`}
            </p>
            <GuideLink
                audience="student"
                className="d-block small mb-3"
                short
            />
            {!loading &&
                (windowClosed ? (
                    <StudentWindowClosed
                        term={currentTerm}
                        programs={[program]}
                    />
                ) : (
                    <StudentWindowCloses
                        term={currentTerm}
                        programs={[program]}
                        className="text-body-secondary"
                    />
                ))}
            <PreviewLockNote locked={locked} />
            {!loading && readFailed && (
                <Alert variant="warning">
                    <p className="mb-2">
                        We couldn&apos;t check your application, so Apply is
                        unavailable. Try again.
                    </p>
                    <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={retryReads}
                    >
                        Try again
                    </Button>
                </Alert>
            )}
            {!loading && !positionsFailed && notice && (
                <Alert variant="info">
                    {notice.text}
                    {notice.link && (
                        <>
                            {' '}
                            <Alert.Link as={Link} to={notice.link.to}>
                                {notice.link.text}
                            </Alert.Link>
                        </>
                    )}
                </Alert>
            )}
            {body}
        </Container>
    );
}
